/**
 * Floating Add to Cart
 * Shows a fixed add to cart button at the bottom of the viewport
 * when the main add to cart button scrolls out of view.
 * Hides when at the bottom of the page.
 */
class FloatingAddToCart extends HTMLElement {
  constructor() {
    super();
    this.mainButton = null;
    this.floatingButton = this.querySelector('[data-floating-add-to-cart]');
    this.floatingLabel = this.querySelector('[data-floating-label]');
    this.observer = null;
    this.mainButtonVisible = true;
    this.isAtBottom = false;
    this.scrollHandler = this.checkIfAtBottom.bind(this);
  }

  connectedCallback() {
    this.mainButton = document.querySelector('[data-add-to-cart]');
    if (!this.mainButton || !this.floatingButton) return;

    this.setupIntersectionObserver();
    this.setupClickHandler();
    this.setupMutationObserver();
    this.setupScrollListener();
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }
    window.removeEventListener('scroll', this.scrollHandler);
  }

  /**
   * Sets up intersection observer to detect when main button leaves viewport
   */
  setupIntersectionObserver() {
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.mainButtonVisible = true;
          } else {
            var rect = this.mainButton.getBoundingClientRect();
            this.mainButtonVisible = rect.bottom > 0;
          }
          this.updateVisibility();
        });
      },
      {
        threshold: 0,
        rootMargin: '0px'
      }
    );

    this.observer.observe(this.mainButton);
  }

  /**
   * Sets up scroll listener to detect when at bottom of page
   */
  setupScrollListener() {
    window.addEventListener('scroll', this.scrollHandler, { passive: true });
    this.checkIfAtBottom();
  }

  /**
   * Checks if user has scrolled to the bottom of the page
   */
  checkIfAtBottom() {
    var scrollPosition = window.scrollY + window.innerHeight;
    var pageHeight = document.documentElement.scrollHeight;
    var threshold = 50;

    this.isAtBottom = scrollPosition >= pageHeight - threshold;
    this.updateVisibility();
  }

  /**
   * Updates floating button visibility based on main button and scroll position
   */
  updateVisibility() {
    var hideWhenDisabled = this.hasAttribute('data-hide-disabled');

    if (!this.mainButtonVisible && !this.isAtBottom) {
      if (hideWhenDisabled && this.mainButton.disabled) {
        this.hide();
      } else {
        this.show();
      }
    } else {
      this.hide();
    }
  }

  /**
   * Sets up click handler to trigger main button click
   */
  setupClickHandler() {
    this.floatingButton.addEventListener('click', () => {
      this.mainButton.click();
    });
  }

  /**
   * Sets up mutation observer to sync content changes from main button
   */
  setupMutationObserver() {
    var mainLabel = this.mainButton.querySelector('[data-add-to-cart-label]');
    if (!mainLabel || !this.floatingLabel) return;

    this.mutationObserver = new MutationObserver(() => {
      this.syncContent();
    });

    this.mutationObserver.observe(mainLabel, {
      childList: true,
      characterData: true,
      subtree: true
    });

    this.syncContent();
  }

  /**
   * Syncs content from main button to floating button
   */
  syncContent() {
    var mainLabel = this.mainButton.querySelector('[data-add-to-cart-label]');
    if (mainLabel && this.floatingLabel) {
      this.floatingLabel.textContent = mainLabel.textContent;
    }

    this.floatingButton.disabled = this.mainButton.disabled;
    this.updateVisibility();
  }

  /**
   * Shows the floating button
   */
  show() {
    this.classList.add('floating-add-to-cart--visible');
  }

  /**
   * Hides the floating button
   */
  hide() {
    this.classList.remove('floating-add-to-cart--visible');
  }
}

customElements.define('floating-add-to-cart', FloatingAddToCart);
